import { ECalendarViewType } from '@constants';
import FullCalendar from '@fullcalendar/react';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { create as createStore } from 'zustand';

import { useEventFilters } from '@/hooks';

type IStore = {
  date?: string;
  setDate: (date?: string) => void;
  currentView: ECalendarViewType;
  setCurrentView: (currentView?: ECalendarViewType) => void;
};

const store = createStore<IStore>(set => ({
  setDate: date => set({ date }),
  currentView: ECalendarViewType.DAY_GRID_MONTH,
  setCurrentView: currentView => set({ currentView })
}));

type UseCalendarOptions = {
  syncView?: boolean;
};

export const useCalendar = (
  calendarRef: React.RefObject<FullCalendar>,
  options?: UseCalendarOptions
) => {
  const { date, setDate, currentView, setCurrentView } = store();
  const { setFilters, filters } = useEventFilters();
  const navigateTo = useNavigate();
  const { syncView = true } = options ?? {};

  const calendarApi = calendarRef.current?.getApi();

  useEffect(() => {
    setDate(calendarApi?.view.currentStart.toString());
  }, [calendarApi?.view.currentStart.toString()]);

  useEffect(() => {
    if (!syncView) return;
    if (!calendarApi || !currentView) return;
    if (calendarApi?.view?.type === currentView) return;
    calendarApi.changeView(currentView);
  }, [syncView, calendarApi, currentView]);

  const handleNext = () => {
    const currentStart = calendarApi?.view.currentStart;
    setDate(currentStart?.toString());
    setFilters({
      ...filters,
      month: dayjs(currentStart).month(),
      year: dayjs(currentStart).year()
    });
    calendarApi?.next();
  };
  const handlePrev = () => {
    const currentStart = calendarApi?.view.currentStart;
    setDate(currentStart?.toString());
    setFilters({
      ...filters,
      month: dayjs(currentStart).month(),
      year: dayjs(currentStart).year()
    });
    calendarApi?.prev();
  };
  const handleToday = () => {
    const currentStart = calendarApi?.view.currentStart;
    setDate(currentStart?.toString());
    setFilters({
      ...filters,
      month: dayjs(currentStart).month(),
      year: dayjs(currentStart).year()
    });
    calendarApi?.today();
  };

  const handleChangeView = (view: ECalendarViewType) => {
    setCurrentView(view);
    if (!syncView) return;
    calendarApi?.changeView(view);
  };

  const title = useMemo(() => calendarApi?.view.title, [currentView, date]);

  const isDayGridMonth = useMemo(
    () => currentView === ECalendarViewType.DAY_GRID_MONTH,
    [currentView]
  );

  const isListWeek = useMemo(() => currentView === ECalendarViewType.LIST_WEEK, [currentView]);

  const goToEvent = (eventId: string) => {
    navigateTo(`/content/calendar/${eventId}`);
  };

  return {
    currentView,
    isDayGridMonth,
    isListWeek,
    handleNext,
    handlePrev,
    handleToday,
    handleChangeView,
    title,
    goToEvent
  };
};
